import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { default as api, type User } from '../service/api';
import { createPersistStorage } from './utils/persist';

interface ManagerUsersState {
  users: User[];
  userBalances: Record<string, number>;
  isLoading: boolean;
  error: string | null;
}

interface ManagerUsersActions {
  fetchUsers: () => Promise<void>;
  updateUserBalance: (userId: string, amount: number) => Promise<void>;
  clearUsers: () => void;
}

type ManagerUsersStore = ManagerUsersState & ManagerUsersActions;

type ManagerUsersStorePersist = Pick<
  ManagerUsersStore,
  'users' | 'userBalances'
>;

const useManagerUsersStore = create<ManagerUsersStore>()(
  persist<
    ManagerUsersStore,
    [],
    [],
    ManagerUsersStorePersist
  >((set, get) => ({
      users: [],
      userBalances: {},
      isLoading: false,
      error: null,

      fetchUsers: async () => {
        const hasCachedUsers = get().users.length > 0;
        set({ isLoading: !hasCachedUsers, error: null });

        try {
          const users = await api.usersGetAll();

          // Fetch balances for all users
          const balances: Record<string, number> = {};
          for (const user of users) {
            try {
              const balance = await api.getUserBalance(user._id);
              balances[user._id] = balance;
            } catch {
              balances[user._id] = 0;
            }
          }

          set({
            users,
            userBalances: balances,
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error ? error.message : 'Failed to fetch users',
            isLoading: false,
          });
        }
      },

      updateUserBalance: async (userId: string, amount: number) => {
        try {
          await api.createCustomTransaction({
            destinationId: userId,
            amount,
            description:
              amount > 0
                ? `Manager added ${amount} stickers`
                : `Manager removed ${Math.abs(amount)} stickers`,
          });

          // Update local balance
          const newBalance = await api.getUserBalance(userId);
          set(state => ({
            userBalances: {
              ...state.userBalances,
              [userId]: newBalance,
            },
          }));
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to update balance',
          );
        }
      },

      clearUsers: () =>
        set({
          users: [],
          userBalances: {},
          error: null,
        }),
    }),
    {
      name: 'manager-users-store',
      storage: createPersistStorage<ManagerUsersStorePersist>(),
      partialize: state => ({
        users: state.users,
        userBalances: state.userBalances,
      }),
    },
  ),
);

export default useManagerUsersStore;
